import { useState } from "react";
import { AlertTriangle, CheckCheck, ChevronDown, History, MessagesSquare, Radio, ShoppingCart, Undo2, Ban, CheckCircle2 } from "lucide-react";
import { NUM, fmtDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { UnreadDot } from "@/components/live/LivePrepBar";

const sign = (n) => (n > 0 ? `+${NUM(n)}` : NUM(n));
const delOf = (i) => (i.delivered != null ? Number(i.delivered) : (i.prepared ? (Number(i.quantity) || 0) : 0));

// Canlı sepet listesi üst bandı: CANLI SEPET / satış tamamlandı / sepet iptal + mesaj düğmesi
export function LiveCartBanner({ list, onChat }) {
  const st = list.cart_status || "open";
  const unread = list.msg_unread || 0;
  const cls = st === "open" ? "bg-red-600" : st === "completed" ? "bg-emerald-600" : "bg-slate-600";
  return (
    <div className={cn("flex items-center gap-2 px-4 pl-5 py-2 text-white", cls)} data-testid={`prep-live-banner-${list.id}`}>
      {st === "open" ? <span className="relative flex h-2.5 w-2.5 shrink-0"><span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 animate-ping" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" /></span>
        : st === "completed" ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <Ban className="h-4 w-4 shrink-0" />}
      <div className="min-w-0 flex-1 leading-tight">
        <div className="text-[11px] font-black uppercase tracking-[0.14em] flex items-center gap-1.5">
          {st === "open" ? <><Radio className="h-3.5 w-3.5" />Canlı Sepet</> : st === "completed" ? `Satış tamamlandı · ${list.sale_no || ""}` : "Sepet iptal edildi"}
        </div>
        <div className="text-[10.5px] text-white/85 truncate">
          {st === "open" ? <><ShoppingCart className="inline h-3 w-3 mr-0.5 -mt-px" />{list.cart_owner_name || "—"} sepeti · değişiklikler anında yansır</>
            : st === "completed" ? "Sepet kapandı — liste son haliyle sabitlendi" : "Hazırlanan ürünleri rafa geri koyun"}
        </div>
      </div>
      <button type="button" onClick={() => onChat(list)} className={cn("relative inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[11px] font-bold transition-all active:scale-95", unread ? "bg-white text-red-700 shadow-lg" : "bg-white/15 hover:bg-white/25")} data-testid={`prep-chat-${list.id}`}>
        <MessagesSquare className="h-4 w-4" />Sepete Mesaj
        {unread > 0 && <UnreadDot n={unread} testid={`prep-chat-unread-${list.id}`} />}
      </button>
    </div>
  );
}

// "Sepette değişiklik var" uyarısı — tümünü gördüm
export function ChangesAlert({ list, count, onAckAll }) {
  if (!count) return null;
  return (
    <div className="mb-3 rounded-xl border border-orange-300 bg-orange-50 px-3 py-2 flex items-center gap-2.5 animate-in fade-in duration-300" data-testid={`prep-changes-alert-${list.id}`}>
      <AlertTriangle className="h-4 w-4 text-orange-600 shrink-0" />
      <div className="min-w-0 flex-1 leading-tight">
        <div className="text-[12px] font-black uppercase tracking-wider text-orange-800">Sepette değişiklik var · {count} kalem</div>
        <div className="text-[11px] text-orange-700">İşaretli kalemleri kontrol edin. Onaylanmadan liste tamamlanamaz.</div>
      </div>
      <button type="button" onClick={() => onAckAll(list)} className="shrink-0 inline-flex h-8 items-center gap-1 rounded-lg bg-orange-600 text-white px-2.5 text-[11px] font-bold active:scale-95" data-testid={`prep-ack-all-${list.id}`}><CheckCheck className="h-4 w-4" />Tümünü Gördüm</button>
    </div>
  );
}

// Kalem değişiklik rozeti: 22 → 28 · +6 ADET DAHA EKLENECEK / YENİ / AZALDI / SEPETTEN ÇIKARILDI
export function ItemChangeBadge({ it, unit, onAck, testid }) {
  const ch = it.change;
  if (!ch) return null;
  const d = delOf(it), q = Number(it.quantity) || 0;
  let label, sub, tone = "bg-orange-600", ackLabel = "Gördüm";
  if (ch.kind === "removed") {
    label = `SEPETTEN ÇIKARILDI · ${NUM(ch.from)} ${unit}`;
    sub = d > 0 ? `Hazırlanan ${NUM(d)} ${unit} rafa geri konacak` : "Bu ürünü hazırlamayın";
    tone = "bg-red-600"; ackLabel = d > 0 ? "Geri Koydum" : "Gördüm";
  } else if (ch.kind === "added") {
    label = `YENİ EKLENDİ · ${NUM(q)} ${unit}`;
    sub = "Sepete sonradan eklendi"; tone = "bg-violet-600";
  } else if (ch.delta > 0) {
    label = `DEĞİŞTİ: ${NUM(ch.from)} → ${NUM(ch.to)} · ${sign(ch.delta)} ${unit} DAHA EKLENECEK`;
    sub = d > 0 ? `${NUM(d)} hazırlandı · ${NUM(Math.max(0, q - d))} ${unit} daha eklenecek` : "Yeni miktara göre hazırlayın";
  } else {
    label = `AZALDI: ${NUM(ch.from)} → ${NUM(ch.to)} (${sign(ch.delta)} ${unit})`;
    sub = d > q ? `Fazla hazırlanan ${NUM(d - q)} ${unit} rafa geri konacak` : "Yeni miktara göre hazırlayın";
    tone = "bg-amber-600"; ackLabel = d > q ? "Geri Koydum" : "Gördüm";
  }
  return (
    <div className="mt-1 flex items-center gap-x-2 gap-y-1 flex-wrap" data-testid={testid}>
      <span className={cn("inline-flex items-center rounded-md text-white px-2 py-0.5 text-[11px] font-bold leading-4", tone)}>{label}</span>
      <span className="text-[11px] font-semibold text-foreground/80">{sub}</span>
      <span className="text-[10px] text-muted-foreground">{ch.by ? `${ch.by} · ` : ""}{fmtDateTime(ch.at)}</span>
      <button type="button" onClick={onAck} className="ml-auto inline-flex h-7 items-center gap-1 rounded-md border border-border bg-card px-2 text-[11px] font-bold hover:bg-muted active:scale-95" data-testid={`${testid}-ack`}>
        {ackLabel === "Geri Koydum" ? <Undo2 className="h-3.5 w-3.5" /> : <CheckCheck className="h-3.5 w-3.5" />}{ackLabel}
      </button>
    </div>
  );
}

// Sepet değişiklik geçmişi (kim, ne zaman, hangi ürün)
export function ChangeHistory({ list }) {
  const [open, setOpen] = useState(false);
  const rows = [...(list.changes || [])].reverse();
  if (!rows.length) return null;
  return (
    <div className="mt-3 rounded-lg border border-dashed" data-testid={`prep-change-history-${list.id}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground" data-testid={`prep-change-history-toggle-${list.id}`}>
        <History className="h-3.5 w-3.5" />Sepet değişiklik geçmişi ({rows.length})<ChevronDown className={cn("h-3.5 w-3.5 ml-auto transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="px-3 pb-2 max-h-48 overflow-y-auto divide-y divide-border/60 text-[11px]">
          {rows.map((r, i) => (
            <div key={i} className="py-1 flex items-center gap-2">
              <span className="tnum text-muted-foreground w-28 shrink-0">{fmtDateTime(r.at)}</span>
              <span className="flex-1 min-w-0 truncate">
                <b>{r.name}</b>{" "}
                {r.kind === "added" ? `eklendi (${NUM(r.to)})` : r.kind === "removed" ? "sepetten çıkarıldı" : r.kind === "ack" ? "" : `${NUM(r.from)} → ${NUM(r.to)} (${sign(r.delta)})`}
              </span>
              <span className="text-muted-foreground shrink-0">{r.by}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
